import { useFileDataMutation } from "@/api/campaign.api";
import Badge from "@/components/Badge";
import { IndustryFilterFormValues } from "@/components/filters/IndustryFilter";
import { Checkbox } from "@/components/generic/Checkbox";
import { Input } from "@/components/generic/Field";
import { Focusable, Tooltip } from "@/components/generic/Tooltip";
import { focusRing } from "@/components/generic/utils";
import {
  findIndustryCodeDeep,
  industryCodes,
  type IndustryCode,
} from "@/data/industry-codes";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Loader2, UploadIcon } from "lucide-react";
import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Button as RACButton,
  Checkbox as RACCheckbox,
} from "react-aria-components";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

type FilteredIndustryCode = IndustryCode & {
  filteredChildren?: FilteredIndustryCode[];
};

type SelectedIndustriesByCategory = Map<
  string,
  { title: string; ids: Map<string, string> }
>;

function flattenSelectedIndustries(
  selected: SelectedIndustriesByCategory,
): string[] {
  const ids: string[] = [];
  for (const { ids: categoryIds } of selected.values()) {
    ids.push(...categoryIds.keys());
  }
  return ids;
}

export function IndustrySelect({
  selectedIndustries,
  setSelectedIndustries,
}: {
  selectedIndustries: SelectedIndustriesByCategory;
  setSelectedIndustries: Dispatch<
    React.SetStateAction<SelectedIndustriesByCategory>
  >;
}) {
  const { control, setValue, trigger } =
    useFormContext<IndustryFilterFormValues>();

  const [query, setQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: getFileData, isPending } = useFileDataMutation();

  const selectedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const { ids: categoryIds } of selectedIndustries.values()) {
      for (const id of categoryIds.keys()) {
        ids.add(id);
      }
    }
    return ids;
  }, [selectedIndustries]);

  const [expandedIndustries, setExpandedIndustries] = useState<Set<string>>(
    new Set(),
  );

  const debouncedSearch = useDebounce(query, 400);

  const shouldSearch = useMemo(
    () => debouncedSearch.length >= 3,
    [debouncedSearch],
  );

  const filteredIndustryCodes = useMemo(() => {
    const searchTerm = shouldSearch
      ? debouncedSearch.toLowerCase().replaceAll(".", "")
      : "";

    const filterIndustryCodes = (
      industries: IndustryCode[],
    ): FilteredIndustryCode[] =>
      industries
        .map((industry) => {
          const filteredChildren =
            industry.children && filterIndustryCodes(industry.children);

          if (searchTerm && !filteredChildren?.length) {
            const industryString = `${industry.id}: ${industry.name}`
              .toLowerCase()
              .replaceAll(".", "");
            if (!industryString.includes(searchTerm)) return null;
          }

          return {
            ...industry,
            filteredChildren,
          };
        })
        .filter((industry) => industry !== null);

    return filterIndustryCodes(industryCodes);
  }, [debouncedSearch, shouldSearch]);

  useEffect(() => {
    const newExpandedIndustries = new Set<string>();

    if (shouldSearch) {
      const processIndustries = (industries: FilteredIndustryCode[]) => {
        for (const industry of industries) {
          newExpandedIndustries.add(industry.id);
          if (industry.filteredChildren)
            processIndustries(industry.filteredChildren);
        }
      };
      processIndustries(filteredIndustryCodes);
    }

    setExpandedIndustries(newExpandedIndustries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredIndustryCodes]);

  useEffect(() => {
    setValue("industries", flattenSelectedIndustries(selectedIndustries), {
      shouldDirty: true,
    });
    trigger("industries");
  }, [selectedIndustries, setValue, trigger]);

  const totalSelected = useMemo(() => selectedIds.size, [selectedIds]);

  const findRootIndustry = useCallback(
    (industryId: string): IndustryCode | undefined => {
      for (const root of industryCodes) {
        if (findIndustryCodeDeep([root], industryId)) {
          return root;
        }
      }
      return undefined;
    },
    [],
  );

  const processIndustryCodes = useCallback(
    (items: string[]) => {
      let addedCount = 0;

      setSelectedIndustries((prev) => {
        const newMap = new Map(prev);

        for (const item of items) {
          const trimmedId = item.trim();
          if (!trimmedId) continue;

          const industry = findIndustryCodeDeep(industryCodes, trimmedId);
          if (!industry) continue;

          const rootIndustry = findRootIndustry(trimmedId);
          if (!rootIndustry) continue;

          if (!industry.children) {
            const existing = newMap.get(rootIndustry.id);
            if (existing) {
              if (!existing.ids.has(industry.id)) {
                existing.ids.set(industry.id, industry.name);
                addedCount++;
              }
            } else {
              newMap.set(rootIndustry.id, {
                title: rootIndustry.name,
                ids: new Map([[industry.id, industry.name]]),
              });
              addedCount++;
            }
          }
        }

        return newMap;
      });

      if (addedCount === 0) {
        toast.error("Ingen gyldige branchekoder fundet i filen.");
      }
      if (addedCount > 0) {
        toast.success(`${addedCount} branchekoder tilføjet.`);
      }
    },
    [findRootIndustry, setSelectedIndustries],
  );

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = "";

    const response = await getFileData({ file, options: { hasHeaders: true } });
    processIndustryCodes(response);
  };

  const transitionConfig = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  function formatString(str: string): string {
    const firstLetter = str.charAt(0).toUpperCase();

    const restOfString = str.slice(1).toLowerCase();

    return firstLetter + restOfString;
  }

  return (
    <div className="flex w-full grow flex-col gap-y-4">
      <div className="flex flex-1 items-center justify-between gap-x-4">
        <div>
          <span className="text-sm text-nowrap">
            {totalSelected} {totalSelected === 1 ? "branche" : "brancher"} valgt
          </span>
        </div>
        <div className="flex h-14 items-stretch gap-x-4">
          <Controller
            control={control}
            name="operator"
            render={({ field }) => (
              <Checkbox
                size="xs"
                className="flex items-center"
                isSelected={field.value === "NotEqual"}
                onChange={(checked) =>
                  field.onChange(checked ? "NotEqual" : "Equal")
                }
              >
                <span className="text-theme-on-background-variant text-sm text-nowrap">
                  Ekskludér valg fra målgruppen
                </span>
              </Checkbox>
            )}
          />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            wrapperClassName="min-w-0 w-68"
            className="h-full p-4 text-sm"
            placeholder="Søg branche"
            showIcon={false}
          />
          <Tooltip tooltipText="Upload CSV/Excel fil med branchekoder">
            <Focusable>
              <RACButton
                className="border-theme-outline-variant hover:bg-theme-on-background-hover flex h-full w-14 cursor-pointer items-center justify-center rounded-md border transition-colors"
                onPress={() => fileInputRef.current?.click()}
                isDisabled={isPending}
              >
                {isPending ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <UploadIcon size={20} />
                )}
              </RACButton>
            </Focusable>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>
      <div className="border-theme-outline-variant flex shrink grow flex-col rounded-md border text-[11px]/[16px] font-medium">
        {filteredIndustryCodes.map((industry) => (
          <IndustryTreeItem
            key={industry.id}
            industry={industry}
            rootIndustry={industry}
            level={0}
            selectedIds={selectedIds}
            setSelectedIndustries={setSelectedIndustries}
            expandedIndustries={expandedIndustries}
            setExpandedIndustries={setExpandedIndustries}
            searchQuery={debouncedSearch}
          />
        ))}
      </div>
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <motion.div
            {...transitionConfig}
            className="border-theme-outline-variant flex max-h-80 w-full flex-col gap-6 rounded-md border p-6"
          >
            <div className="grid h-fit w-full grid-cols-2 gap-8 overflow-y-auto text-[11px]/[16px] font-medium">
              {[...selectedIndustries.entries()]
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([categoryId, { title, ids }]) => (
                  <div key={categoryId} className="flex flex-col gap-2">
                    <div className="flex gap-0.5 font-bold">
                      <span className="font-medium">{categoryId}:</span>
                      <span>{formatString(title)}</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {[...ids.entries()]
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([id, name]) => {
                          return (
                            <Tooltip
                              arrow
                              arrowPosition="top"
                              className="mb-2 duration-200"
                              tooltipText={name}
                            >
                              <Focusable>
                                <Badge
                                  className="cursor-pointer"
                                  key={id}
                                  remove={() => {
                                    setSelectedIndustries((prev) => {
                                      const newMap = new Map(prev);
                                      const category = newMap.get(categoryId);
                                      if (category) {
                                        category.ids.delete(id);
                                        if (category.ids.size === 0) {
                                          newMap.delete(categoryId);
                                        }
                                      }
                                      return newMap;
                                    });
                                  }}
                                >
                                  {id}
                                </Badge>
                              </Focusable>
                            </Tooltip>
                          );
                        })}
                    </div>
                  </div>
                ))}
            </div>
            <Controller
              control={control}
              name="includeSubIndustries"
              render={({ field }) => (
                <Checkbox
                  size="xs"
                  className="mr-auto"
                  onChange={field.onChange}
                  isSelected={field.value}
                >
                  <span className="text-theme-on-background-variant text-xs text-nowrap">
                    Match også på bibrancher
                  </span>
                </Checkbox>
              )}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function IndustryTreeItem({
  industry,
  rootIndustry,
  level = 0,
  selectedIds,
  setSelectedIndustries,
  expandedIndustries,
  setExpandedIndustries,
  searchQuery = "",
}: {
  industry: IndustryCode;
  rootIndustry: IndustryCode;
  level: number;
  selectedIds: Set<string>;
  setSelectedIndustries: Dispatch<
    React.SetStateAction<SelectedIndustriesByCategory>
  >;
  expandedIndustries: Set<string>;
  setExpandedIndustries: Dispatch<React.SetStateAction<Set<string>>>;
  searchQuery?: string;
}) {
  const isExpanded = useMemo(
    () => expandedIndustries.has(industry.id),
    [expandedIndustries, industry.id],
  );

  const toggleExpanded = useCallback(() => {
    setExpandedIndustries((prev) => {
      const next = new Set(prev);
      if (next.has(industry.id)) {
        next.delete(industry.id);
      } else {
        next.add(industry.id);
      }
      return next;
    });
  }, [industry.id, setExpandedIndustries]);

  const handleSelection = (checked: boolean, industry: IndustryCode) => {
    // if (checked && industry.children?.length) {
    //   setExpandedIndustries((prev) => {
    //     const next = new Set(prev);
    //     const expandAll = (node: IndustryCode) => {
    //       next.add(node.id);
    //       node.children?.forEach(expandAll);
    //     };
    //     expandAll(industry);
    //     return next;
    //   });
    // }

    setSelectedIndustries((prev) => {
      const newMap = new Map(prev);

      const processIndustry = (industry: IndustryCode) => {
        if (industry.children) {
          for (const childIndustry of industry.children) {
            processIndustry(childIndustry);
          }
          return;
        }
        const existing = newMap.get(rootIndustry.id);

        if (checked) {
          if (existing) {
            existing.ids.set(industry.id, industry.name);
          } else {
            newMap.set(rootIndustry.id, {
              title: rootIndustry.name,
              ids: new Map([[industry.id, industry.name]]),
            });
          }
        } else {
          if (existing) {
            existing.ids.delete(industry.id);
            if (existing.ids.size === 0) {
              newMap.delete(rootIndustry.id);
            }
          }
        }
      };

      processIndustry(industry);

      return newMap;
    });
  };

  const getChildrenState = useCallback(
    (ind: IndustryCode): { total: number; selected: number } => {
      if (!ind.children?.length) return { total: 0, selected: 0 };

      let total = 0;
      let selected = 0;

      const countLeaves = (node: IndustryCode) => {
        if (!node.children?.length) {
          total++;
          if (selectedIds.has(node.id)) selected++;
        } else {
          node.children.forEach(countLeaves);
        }
      };

      ind.children.forEach(countLeaves);
      return { total, selected };
    },
    [selectedIds],
  );

  const { total, selected } = useMemo(
    () => getChildrenState(industry),
    [getChildrenState, industry],
  );

  const isIndeterminate = useMemo(
    () =>
      industry.children?.length ? selected > 0 && selected < total : false,
    [industry, selected, total],
  );

  const isSelected = useMemo(
    () =>
      industry.children?.length
        ? selected === total && total > 0
        : selectedIds.has(industry.id),
    [industry, selected, total, selectedIds],
  );

  const name = useMemo(
    () =>
      industry.name.charAt(0).toUpperCase() +
      industry.name.slice(1).toLowerCase(),
    [industry],
  );

  const highlightText = useCallback(
    (text: string) => {
      if (!searchQuery || searchQuery.length <= 2) return text;

      const parts = text.split(new RegExp(`(${searchQuery})`, "gi"));
      return (
        <>
          {parts.map((part, i) =>
            part.toLowerCase() === searchQuery.toLowerCase() ? (
              <mark key={i} className="rounded-sm bg-gray-600 px-px text-white">
                {part}
              </mark>
            ) : (
              <span className="z-1">{part}</span>
            ),
          )}
        </>
      );
    },
    [searchQuery],
  );

  function formatNumber(num: string): string {
    return ("0" + num).slice(-2);
  }
  const getChildIdRange = useCallback((industry: IndustryCode): string => {
    if (!industry.children?.length) return "";
    const first = industry.children[0].id;
    const last = industry.children[industry.children.length - 1].id;
    return `${formatNumber(first)}-${formatNumber(last)}`;
  }, []);

  // Skip intermediate nodes with single child of same name
  const skipNode =
    industry.children?.length === 1 &&
    industry.children[0].name === industry.name &&
    !industry.children[0].children;

  const renderItem = useCallback(
    ({
      industry,
      name,
      isSelected,
      isIndeterminate,
      handleSelection,
    }: {
      industry: FilteredIndustryCode;
      name: string;
      isSelected: boolean;
      isIndeterminate: boolean;
      handleSelection: (checked: boolean, industry: IndustryCode) => void;
    }) => {
      const renderItemButton = (children: React.ReactNode) => {
        const className =
          "cursor-pointer flex items-center gap-x-4 w-full min-h-9 pl-3 rounded-lg text-left transition-[background-color] duration-200 hover:bg-theme-on-background-hover";

        if (industry.children) {
          return (
            <Button
              className={(renderProps) =>
                twMerge(focusRing(renderProps), className)
              }
              type="button"
              onPress={toggleExpanded}
              aria-label={`${isExpanded ? "Collapse" : "Expand"} ${name}`}
            >
              {children}
            </Button>
          );
        }

        return (
          <RACCheckbox
            className={(renderProps) =>
              twMerge(focusRing(renderProps), className)
            }
            isIndeterminate={isIndeterminate}
            isSelected={isSelected}
            onChange={(checked) => handleSelection(checked, industry)}
            aria-label={`${industry.id}: ${name}`}
          >
            {children}
          </RACCheckbox>
        );
      };

      return (
        <div className="flex flex-col">
          <div className="relative">
            {renderItemButton(
              <>
                <div className="flex items-center gap-x-2">
                  <div className="flex size-4 shrink-0 items-center justify-center">
                    {industry.children && (
                      <ChevronDown
                        aria-hidden
                        className={clsx(
                          "size-4 transition duration-300 ease-in-out",
                          isExpanded ? "rotate-0" : "-rotate-90",
                        )}
                      />
                    )}
                  </div>

                  <Checkbox
                    size="xs"
                    className="size-5"
                    isIndeterminate={isIndeterminate}
                    isSelected={isSelected}
                    onChange={(checked) => handleSelection(checked, industry)}
                    aria-label={`${industry.id}: ${name}`}
                  />
                </div>

                <div className="flex items-center gap-x-2 py-2">
                  <span
                    className={twMerge(
                      "text-theme-on-background-variant font-bold",
                      level === 0 && "text-green-600 dark:text-green-400",
                    )}
                  >
                    {industry.id}
                  </span>
                  {level === 0 && industry.children && (
                    <span className="text-theme-on-background-variant font-medium text-nowrap">
                      ({getChildIdRange(industry)})
                    </span>
                  )}
                  <span className="font-bold">{highlightText(name)}</span>
                </div>
              </>,
            )}
          </div>

          {industry.filteredChildren && isExpanded && (
            <div className="pl-6">
              {industry.filteredChildren?.map((child) => (
                <IndustryTreeItem
                  key={child.id}
                  industry={child}
                  rootIndustry={rootIndustry}
                  level={level + 1}
                  selectedIds={selectedIds}
                  setSelectedIndustries={setSelectedIndustries}
                  expandedIndustries={expandedIndustries}
                  setExpandedIndustries={setExpandedIndustries}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          )}
        </div>
      );
    },
    [
      expandedIndustries,
      getChildIdRange,
      highlightText,
      isExpanded,
      level,
      rootIndustry,
      searchQuery,
      selectedIds,
      setExpandedIndustries,
      setSelectedIndustries,
      toggleExpanded,
    ],
  );

  if (skipNode) {
    const child = industry.children?.[0] ?? null;

    if (!child) return null;

    const isSelected =
      selectedIds.has(child.id) || selectedIds.has(industry.id);

    return renderItem({
      industry: child,
      name: child.name,
      isSelected,
      isIndeterminate: false,
      handleSelection: (checked) => {
        setSelectedIndustries((prev) => {
          const newMap = new Map(prev);
          const existing = newMap.get(rootIndustry.id);

          if (checked) {
            if (existing) {
              existing.ids.set(child.id, child.name);
            } else {
              newMap.set(rootIndustry.id, {
                title: rootIndustry.name,
                ids: new Map([[child.id, child.name]]),
              });
            }
          } else {
            if (existing) {
              existing.ids.delete(child.id);
              existing.ids.delete(industry.id);
              if (existing.ids.size === 0) {
                newMap.delete(rootIndustry.id);
              }
            }
          }

          return newMap;
        });
      },
    });
  }

  return renderItem({
    industry,
    name,
    isSelected,
    isIndeterminate,
    handleSelection,
  });
}
